import { jsPDF } from "jspdf";
import { formatMoney } from "@/lib/api";
import { PRICING_DISCLAIMER, CATEGORIES, BASE_INFRASTRUCTURE } from "@/lib/pricing";

const TIER_LABELS = { basico: "Básico", normal: "Estándar", premium: "Premium" };
const PDF_PHOTOS = ["/gallery/pdf/capilla.jpg", "/gallery/pdf/jardines.jpg", "/gallery/pdf/carpa.jpg"];

const LEGAL_NOTES = [
    "Exclusividad: Este presupuesto incluye única y exclusivamente lo que se estipula de forma escrita en la descripción de cada proveedor.",
    "Referencialidad: Todos los precios son estrictamente referenciales. La inversión final se ajustará en reunión técnica.",
    "Adicionales: Cualquier requerimiento extra no mencionado deberá ser cotizado por el proveedor de forma independiente.",
    "Reserva: Este documento no garantiza la reserva de la fecha. Solo el depósito y contrato bloquean la fecha.",
    "Locación: Hacienda Nápoles opera exclusivamente como locación. No asume responsabilidad solidaria por los servicios de terceros; cada proveedor firma contrato independiente con el cliente.",
];

const COLOR_PRIMARY = [39, 63, 91];
const COLOR_ACCENT = [111, 72, 28];
const COLOR_MUTED = [127, 113, 95];
const COLOR_BG = [246, 241, 229];

let LOGO_DATA_URL = null;
let LOGO_RATIO = 1.487; // ancho / alto del logotipo oficial
async function loadLogoDataUrl() {
    if (LOGO_DATA_URL) return LOGO_DATA_URL;
    try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = "/logo-beige.png";
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
        });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        LOGO_RATIO = img.naturalWidth / img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        LOGO_DATA_URL = canvas.toDataURL("image/png");
        return LOGO_DATA_URL;
    } catch (_err) {
        return null;
    }
}

const _imgCache = {};
async function loadImageDataUrl(url, type = "image/jpeg") {
    if (_imgCache[url]) return _imgCache[url];
    try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = url;
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        _imgCache[url] = canvas.toDataURL(type, 0.75);
        return _imgCache[url];
    } catch (_err) {
        return null;
    }
}

export async function buildQuotePdf({ state, breakdown, config }) {
    const logoDataUrl = await loadLogoDataUrl();
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 48;
    let y = M;

    // HEADER
    doc.setFillColor(...COLOR_PRIMARY);
    doc.rect(0, 0, pageW, 140, "F");
    doc.setTextColor(...COLOR_BG);
    if (logoDataUrl) {
        try {
            const lh = 82;
            const lw = lh * LOGO_RATIO;
            doc.addImage(logoDataUrl, "PNG", M, 29, lw, lh);
        } catch (_err) { /* noop */ }
    }
    doc.setFont("times", "normal");
    doc.setFontSize(24);
    doc.text("Hacienda Nápoles", M + 148, 62);
    doc.setFontSize(9);
    doc.setFont("courier", "normal");
    doc.text("PUEMBO · ECUADOR · COTIZACIÓN REFERENCIAL MIX & MATCH", M + 148, 82);
    doc.text(`Emitida: ${new Date().toLocaleDateString("es-EC")}`, M + 148, 100);
    if (state.session_quote_id) {
        doc.text(`ID sesión: ${String(state.session_quote_id).slice(0, 8)}`, M + 148, 116);
    }

    y = 170;
    doc.setTextColor(...COLOR_PRIMARY);

    // TITULAR
    const isWedding = state.event_type === "Boda" && state.bride_name && state.groom_name;
    const title = isWedding
        ? `${state.bride_name}  &  ${state.groom_name}`
        : `${state.event_type || "Evento"} — ${state.contact_name || ""}`;
    doc.setFont("times", "italic");
    doc.setFontSize(24);
    doc.text(title, M, y);
    y += 22;
    doc.setFont("courier", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(`Contacto: ${state.contact_name || "—"}  |  Tipo: ${state.event_type || "—"}  |  Invitados: ${state.guests || 0}`, M, y);
    y += 14;
    const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    const dateStr = state.date_mode === "exact"
        ? (state.exact_date || "—")
        : `Meses tentativos: ${(state.tentative_months || []).map((m) => MONTHS_ES[m - 1]).join(", ") || "—"}`;
    doc.text(`Fecha: ${dateStr}  |  Escenarios: ${humanLocations(state.locations)}`, M, y);
    y += 22;

    // Cláusula referencial (inicio)
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_ACCENT);
    doc.splitTextToSize(PRICING_DISCLAIMER, pageW - 2 * M).forEach((ln) => { doc.text(ln, M, y); y += 11; });
    y += 8;

    // DETALLE
    doc.setDrawColor(...COLOR_PRIMARY);
    doc.line(M, y, pageW - M, y);
    y += 16;
    doc.setTextColor(...COLOR_PRIMARY);
    doc.setFont("times", "normal");
    doc.setFontSize(14);
    doc.text("Detalle de Inversión", M, y);
    y += 16;

    doc.setFont("courier", "normal");
    doc.setFontSize(9.5);
    (breakdown.lines || []).forEach((l) => {
        if (y > pageH - 220) { doc.addPage(); y = M; }
        const amount = (l.amount || 0) + (l.surcharge || 0);
        doc.setTextColor(...COLOR_PRIMARY);
        doc.text(wrap(l.label, 70), M, y);
        doc.text(formatMoney(amount), pageW - M, y, { align: "right" });
        y += 13;
        if (l.note) {
            doc.setTextColor(...COLOR_MUTED);
            doc.text(wrap(l.note, 90), M + 10, y);
            y += 11;
        }
        if (l.surcharge_label) {
            doc.setTextColor(...COLOR_ACCENT);
            doc.text(l.surcharge_label, M + 10, y);
            y += 11;
        }
    });

    if (breakdown.carpa_included_in_wp) {
        doc.setTextColor(...COLOR_ACCENT);
        doc.text("Estructura / Carpa: incluida en el paquete Todo Incluido de la Wedding Planner.", M, y);
        y += 13;
    }

    y += 6;
    doc.setDrawColor(...COLOR_PRIMARY);
    doc.line(M, y, pageW - M, y);
    y += 18;
    doc.setTextColor(...COLOR_PRIMARY);
    doc.setFont("times", "normal");
    doc.setFontSize(18);
    doc.text("Total Referencial", M, y);
    doc.text(formatMoney(breakdown.total), pageW - M, y, { align: "right" });
    y += 22;

    if (breakdown.carpa) {
        doc.setFont("courier", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_MUTED);
        doc.text(
            `Carpa modular ${breakdown.carpa.width}m: ${breakdown.carpa.modules} módulo(s) · ${breakdown.carpa.area_m2} m² · flete estimado ${formatMoney(breakdown.carpa.flete)}`,
            M, y,
        );
        y += 12;
        if (breakdown.staff) {
            doc.text(`Personal operativo incluido (aseo + parqueadero): ${breakdown.staff} personas`, M, y);
            y += 12;
        }
        y += 6;
    }

    // FOTOS DE LA HACIENDA
    const photos = (await Promise.all(PDF_PHOTOS.map((p) => loadImageDataUrl(p)))).filter(Boolean);
    if (photos.length) {
        if (y > pageH - 180) { doc.addPage(); y = M; }
        const gap = 10;
        const pw = (pageW - 2 * M - gap * (photos.length - 1)) / photos.length;
        const ph = pw * (2 / 3);
        photos.forEach((ph64, i) => {
            try { doc.addImage(ph64, "JPEG", M + i * (pw + gap), y, pw, ph); } catch (_err) { /* noop */ }
        });
        y += ph + 18;
    }

    // INCLUYE LA HACIENDA
    if (y > pageH - 200) { doc.addPage(); y = M; }
    doc.setTextColor(...COLOR_PRIMARY);
    doc.setFont("times", "normal");
    doc.setFontSize(13);
    doc.text("Incluye Hacienda Nápoles (cortesía de la locación)", M, y);
    y += 14;
    doc.setFont("courier", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR_MUTED);
    BASE_INFRASTRUCTURE.forEach((item) => {
        const lines = doc.splitTextToSize(`• ${item.label}: ${item.description}`, pageW - 2 * M);
        lines.forEach((ln) => {
            if (y > pageH - 120) { doc.addPage(); y = M; }
            doc.text(ln, M, y);
            y += 10;
        });
    });
    y += 10;

    // DETALLE DE PROVEEDORES SELECCIONADOS
    const selectedProviders = CATEGORIES
        .map((c) => ({ cat: c, p: (config?.providers || []).find((x) => x.id === state.providers?.[c.key]) }))
        .filter((x) => x.p);
    if (selectedProviders.length) {
        if (y > pageH - 200) { doc.addPage(); y = M; }
        doc.setTextColor(...COLOR_PRIMARY);
        doc.setFont("times", "normal");
        doc.setFontSize(13);
        doc.text("Detalle de los paquetes seleccionados", M, y);
        y += 14;
        selectedProviders.forEach(({ cat, p }) => {
            if (y > pageH - 140) { doc.addPage(); y = M; }
            doc.setFont("courier", "normal");
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_PRIMARY);
            doc.text(`${cat.label} — ${p.name} (${TIER_LABELS[p.tier] || p.tier})`, M, y);
            y += 11;
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MUTED);
            (p.included || []).forEach((inc) => {
                if (y > pageH - 110) { doc.addPage(); y = M; }
                doc.text(wrap(`· ${inc}`, 95), M + 10, y);
                y += 9;
            });
            y += 5;
        });
        y += 6;
    }

    // LEGAL
    if (y > pageH - 220) { doc.addPage(); y = M; }
    doc.setTextColor(...COLOR_PRIMARY);
    doc.setFont("times", "normal");
    doc.setFontSize(12);
    doc.text("Notas Legales", M, y);
    y += 14;
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_MUTED);
    LEGAL_NOTES.forEach((n, i) => {
        const lines = doc.splitTextToSize(`${i + 1}. ${n}`, pageW - 2 * M);
        lines.forEach((ln) => {
            if (y > pageH - 150) { doc.addPage(); y = M; }
            doc.text(ln, M, y);
            y += 10;
        });
        y += 3;
    });

    // Cláusula obligatoria al final del PDF
    y += 8;
    if (y > pageH - 180) { doc.addPage(); y = M; }
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_ACCENT);
    doc.splitTextToSize(PRICING_DISCLAIMER, pageW - 2 * M).forEach((ln) => { doc.text(ln, M, y); y += 11; });

    // CIERRE con sello
    if (y > pageH - 160) { doc.addPage(); y = M; }
    y = Math.max(y + 20, pageH - 150);
    if (logoDataUrl) {
        try {
            const sealH = 76;
            const sealW = sealH * LOGO_RATIO;
            doc.setFillColor(...COLOR_PRIMARY);
            doc.rect(pageW / 2 - sealW / 2 - 18, y - 14, sealW + 36, sealH + 28, "F");
            doc.addImage(logoDataUrl, "PNG", pageW / 2 - sealW / 2, y, sealW, sealH);
        } catch (_err) { /* noop */ }
    }
    y += 110;
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    doc.setTextColor(...COLOR_PRIMARY);
    doc.text("Gracias por elegir Hacienda Nápoles.", pageW / 2, y, { align: "center" });

    // Footer numeración + cláusula corta
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        doc.setFont("courier", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...COLOR_MUTED);
        doc.text(`Hacienda Nápoles · Puembo · Precios referenciales — página ${p} de ${pages}`, pageW / 2, pageH - 20, { align: "center" });
    }

    return doc;
}

function wrap(s, max) {
    if (!s) return "";
    if (s.length <= max) return s;
    return s.slice(0, max - 1) + "…";
}

function humanLocations(locs) {
    if (!locs) return "—";
    const arr = Array.isArray(locs) ? locs : String(locs).split(",").filter(Boolean);
    const map = {
        capilla: "Capilla",
        jardines: "Jardines",
        iglesia_externa: "Iglesia Externa",
        isla_reservorio: "La Isla del Reservorio (Premium)",
    };
    return arr.map((k) => map[k] || k).join(" + ") || "—";
}
